import { useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

export function FilterSheet({
  open,
  title,
  onClose,
  children,
  footer,
  closeLabel = 'Close',
}: {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  closeLabel?: string
}) {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open || typeof document === 'undefined') return
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    const panel = panelRef.current
    const focusables = getFocusableElements(panel)
    ;(focusables[0] ?? panel)?.focus()

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      const currentFocusables = getFocusableElements(panelRef.current)
      if (currentFocusables.length === 0) {
        event.preventDefault()
        panelRef.current?.focus()
        return
      }
      const first = currentFocusables[0]
      const last = currentFocusables[currentFocusables.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      previousFocusRef.current?.focus()
    }
  }, [onClose, open])

  if (!open) return null

  const content = (
    <div className="fixed inset-0 z-50 pointer-events-none" data-testid="filter-sheet-root">
      <button
        type="button"
        aria-label={closeLabel}
        className="absolute inset-0 pointer-events-auto cursor-default bg-transparent"
        onClick={onClose}
        data-testid="filter-sheet-scrim"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="pointer-events-auto fixed bottom-0 left-0 right-0 flex max-h-[82dvh] flex-col rounded-t-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] shadow-[var(--xr-shadow-lg)] outline-none sm:bottom-0 sm:left-auto sm:right-0 sm:top-0 sm:h-dvh sm:max-h-none sm:w-[min(28rem,calc(100vw-2rem))] sm:rounded-l-[var(--xr-radius-lg)] sm:rounded-tr-none"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex flex-none items-center justify-between gap-3 border-b border-[var(--xr-border-default)] px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-[var(--xr-text-primary)]">
            {title}
          </h2>
          <button
            type="button"
            className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-full border border-[var(--xr-border-default)] text-sm leading-none text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)]"
            onClick={onClose}
            aria-label={closeLabel}
          >
            ×
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <div className="flex flex-none justify-end gap-2 border-t border-[var(--xr-border-default)] px-5 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  )

  if (typeof document === 'undefined') return content
  return createPortal(content, document.body)
}

function getFocusableElements(root: HTMLElement | null): HTMLElement[] {
  if (!root) return []
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => !element.hasAttribute('disabled') && element.tabIndex !== -1,
  )
}
